import React from "react";
import { Box, Skeleton, Tooltip, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";

/** Shared header-cell look (same as RedemptionProductsTable). */
export const podHeaderCellSx = {
  fontSize: "0.68rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  borderBottom: "1px solid",
  py: 1.1,
};

/** i-icon tooltip, same behaviour as RedemptionHint. */
export const PodRevenueHint = ({
  text,
  fontSize = 14,
}: {
  text?: string | null;
  fontSize?: number;
}) => {
  const theme = useTheme();
  if (!text) return null;
  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 300, fontSize: "0.75rem", lineHeight: 1.45 }}>
          {text}
        </Box>
      }
      arrow
      placement="top"
      slotProps={{ popper: { sx: { zIndex: (t) => t.zIndex.drawer + 5000 } } }}
    >
      <Box
        component="span"
        role="img"
        aria-label={text}
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "help",
          color: theme.palette.customColors.greyTextColor,
          lineHeight: 0,
          flexShrink: 0,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize }} />
      </Box>
    </Tooltip>
  );
};

export const PodRevenueEmptyState = ({
  message,
  error,
  onRetry,
}: {
  message: string;
  error?: boolean;
  onRetry?: () => void;
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const Icon = error ? ErrorOutlineIcon : InboxOutlinedIcon;
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap={1}
      minHeight="10rem"
      textAlign="center"
      border="1px dashed"
      borderColor={error ? colors.errorRed : "divider"}
      borderRadius={2}
      px={2}
      py={2}
      bgcolor={colors.softCardBackground}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: colors.veryLightGray,
          color: error ? colors.errorRed : colors.greyTextColor,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={error ? colors.errorRed : colors.greyTextColor}
      >
        {message}
      </CustomTypography>
      {onRetry ? (
        <AppButton
          label="Retry"
          variant="transparentPrimary"
          onClick={onRetry}
          customStyles={{ minHeight: 30, height: 30, px: 1.5 }}
        />
      ) : null}
    </Box>
  );
};

export const PodRevenueSkeletonCard = ({
  height = 120,
}: {
  height?: number;
}) => <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />;

export const PodRevenueSectionLabel = ({
  label,
  action,
  hint,
}: {
  label: string;
  action?: React.ReactNode;
  hint?: string;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 3, mb: 1.5 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <CustomTypography
          variant="heading8"
          fontWeight={600}
          color={theme.palette.customColors.greyTextColor}
          sx={{ letterSpacing: "0.05em", textTransform: "uppercase" }}
        >
          {label}
        </CustomTypography>
        <PodRevenueHint text={hint} />
      </Box>
      <Box
        sx={{
          flex: 1,
          height: "1px",
          bgcolor: theme.palette.customColors.lightGray,
        }}
      />
      {action}
    </Box>
  );
};
